import {
  ApproverSlot as ApproverSlotEnum,
  BillingEumEditCaseResolution as BillingEumEditCaseResolutionEnum,
  FamilyChangeRequestDecision as FamilyChangeRequestDecisionEnum,
} from "~/graphql/supergraphEnums";
import { BILLING_CSM_MANAGER_ROLE, GP_BILLING_ADMIN_ROLE } from "~/utils/userRoles";
import { GP_NEGATIVE_TERMINAL_DECISION } from "../../constants";
import { GpConclusionValue } from "./constants";
import type { GpChangeRequest } from "./types";
import type {
  ApproverSlot,
  BillingEumEditCaseResolution,
  FamilyChangeRequestDecision,
} from "~/graphql/supergraphTypes";
import type { UserRoles } from "~/types/user";

// Role → claimable slot, mirroring the backend's `_claim_slot_for_roles`:
// GP Billing Admin fills the primary (billing) leg, CSM Manager the secondary.
// A caller holding both signals claims PRIMARY. CSM ICs hold no leg.
export const getEligibleClaimSlot = (userRoles: UserRoles): ApproverSlot | null => {
  if (userRoles?.includes(GP_BILLING_ADMIN_ROLE)) {
    return ApproverSlotEnum.Primary;
  }
  if (userRoles?.includes(BILLING_CSM_MANAGER_ROLE)) {
    return ApproverSlotEnum.Secondary;
  }
  return null;
};

export const getSlotHolderId = (
  gpChangeRequest: GpChangeRequest,
  slot: ApproverSlot,
): number | null =>
  slot === ApproverSlotEnum.Primary
    ? gpChangeRequest.billingApproverId
    : gpChangeRequest.csmManagerAssigneeId;

export const getApproverSlot = (
  userId: number | null | undefined,
  gpChangeRequest: GpChangeRequest,
): ApproverSlot | null => {
  if (userId === null || userId === undefined) {
    return null;
  }
  if (userId === gpChangeRequest.billingApproverId) {
    return ApproverSlotEnum.Primary;
  }
  if (userId === gpChangeRequest.csmManagerAssigneeId) {
    return ApproverSlotEnum.Secondary;
  }
  return null;
};

const isConcludingDecision = (decision: FamilyChangeRequestDecision): boolean =>
  decision === FamilyChangeRequestDecisionEnum.Rejected ||
  decision === FamilyChangeRequestDecisionEnum.Invalid;

export const getDisplaySlot = (gpChangeRequest: GpChangeRequest): ApproverSlot => {
  const primaryDecision = getDecisionForSlot(gpChangeRequest, ApproverSlotEnum.Primary);
  const secondaryDecision = getDecisionForSlot(gpChangeRequest, ApproverSlotEnum.Secondary);
  if (isConcludingDecision(primaryDecision)) {
    return ApproverSlotEnum.Primary;
  }
  if (isConcludingDecision(secondaryDecision)) {
    return ApproverSlotEnum.Secondary;
  }
  if (primaryDecision === FamilyChangeRequestDecisionEnum.Pending) {
    return ApproverSlotEnum.Primary;
  }
  if (secondaryDecision === FamilyChangeRequestDecisionEnum.Pending) {
    return ApproverSlotEnum.Secondary;
  }
  return ApproverSlotEnum.Primary;
};

export const areBothLegsPending = (gpChangeRequest: GpChangeRequest): boolean =>
  getDecisionForSlot(gpChangeRequest, ApproverSlotEnum.Primary) ===
    FamilyChangeRequestDecisionEnum.Pending &&
  getDecisionForSlot(gpChangeRequest, ApproverSlotEnum.Secondary) ===
    FamilyChangeRequestDecisionEnum.Pending;

export const getDecisionForSlot = (
  gpChangeRequest: GpChangeRequest,
  slot: ApproverSlot,
): FamilyChangeRequestDecision => {
  const entry = gpChangeRequest.approverDecisions.find((decision) => decision.slot === slot);
  return entry?.decision ?? FamilyChangeRequestDecisionEnum.Pending;
};

const hasResolution = (gpChangeRequest: GpChangeRequest): boolean =>
  gpChangeRequest.primaryResolution !== null || gpChangeRequest.secondaryResolution !== null;

const bothApproved = (gpChangeRequest: GpChangeRequest): boolean =>
  getDecisionForSlot(gpChangeRequest, ApproverSlotEnum.Primary) ===
    FamilyChangeRequestDecisionEnum.Approved &&
  getDecisionForSlot(gpChangeRequest, ApproverSlotEnum.Secondary) ===
    FamilyChangeRequestDecisionEnum.Approved;

export const isCaseTerminal = (gpChangeRequest: GpChangeRequest): boolean => {
  if (
    gpChangeRequest.decision === FamilyChangeRequestDecisionEnum.Rejected ||
    gpChangeRequest.decision === FamilyChangeRequestDecisionEnum.Invalid
  ) {
    return true;
  }
  return bothApproved(gpChangeRequest) && hasResolution(gpChangeRequest);
};

const gpConclusionValues: string[] = Object.values(GpConclusionValue);

export const isGpConclusionValue = (value: string): value is GpConclusionValue =>
  gpConclusionValues.includes(value);

export type GpDecisionInput = {
  decision: FamilyChangeRequestDecision;
  resolution: BillingEumEditCaseResolution | null;
  requiresReason: boolean;
};

export const getDecisionInputForConclusion = (
  conclusion: GpConclusionValue,
): GpDecisionInput => {
  switch (conclusion) {
    case GpConclusionValue.ApprovedCurrentQuarter:
      return {
        decision: FamilyChangeRequestDecisionEnum.Approved,
        resolution: BillingEumEditCaseResolutionEnum.CurrentQuarter,
        requiresReason: false,
      };
    case GpConclusionValue.ApprovedNextQuarter:
      return {
        decision: FamilyChangeRequestDecisionEnum.Approved,
        resolution: BillingEumEditCaseResolutionEnum.NextQuarter,
        requiresReason: false,
      };
    case GpConclusionValue.Approved:
      return {
        decision: FamilyChangeRequestDecisionEnum.Approved,
        resolution: null,
        requiresReason: false,
      };
    case GpConclusionValue.Invalid:
      // The backend only accepts `rejected` as the GP negative decision; the UI copy stays "Invalid".
      return {
        decision: GP_NEGATIVE_TERMINAL_DECISION,
        resolution: null,
        requiresReason: true,
      };
  }
};
